import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { ValidateErrorEntity } from "@rc-component/form/es/interface";
import { App as AntApp, Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useCreateContract, useUpdateContract, useValidateClient } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { FieldConstraintViolation, NormalizedError } from "../../../../../common/types";
import { Client } from "../../../../client/types";
import { clientToCreateUpdateContractClient } from "../../../../client/utils";
import { ContractType } from "../../../enums";
import { useContractValidation } from "../../../hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { CreateUpdateLoanContract, LoanContract } from "../../../types";
import { parseClientsViolationErrors } from "../../../utils";
import ContractFormAgentsSection from "../ContractFormAgentsSection";
import LoanContractFormDataSection from "./sections/LoanContractFormDataSection";
import { LoanContractFormHeaderSection } from "./sections/LoanContractFormHeaderSection";

type Props = {
  initialContract?: LoanContract;
  onCancel?: () => void;
};

export const LoanContractForm = ({ initialContract, onCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const navigate = useNavigate();

  const [clients, setClients] = useState<(Partial<Client> | undefined)[]>(initialContract?.clients || []);
  const [clientsViolationErrors, setClientsViolationErrors] = useState<Map<number, FieldConstraintViolation[]>>(
    () => new Map(),
  );

  const [form] = Form.useForm<CreateUpdateLoanContract>();

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const handleContractValidationChange = useContractValidation({ form, contractId: initialContract?.id });

  const clientValidation = useValidateClient();

  useEffect(() => {
    if (initialContract) {
      const initialContractCopy = structuredClone(initialContract);
      form.setFieldsValue({
        ...initialContractCopy,
        historyRecords: undefined,
        clients: [],
        clientIds: [],
        institution: undefined,
        product: undefined,
        signer: undefined,
        manager: undefined,
        affiliatePartner: undefined,
        attachments: undefined,
        institutionId: initialContractCopy.institution.id,
        productId: initialContractCopy.product.id,
        clientIdentifiers: initialContractCopy.clients.map((client) => client.identifier),
        signerId: initialContractCopy.signer?.id,
        managerId: initialContractCopy.manager?.id,
        affiliatePartnerId: initialContractCopy.affiliatePartner?.id,
        gainerRecords: initialContractCopy.gainerRecords?.map((record) => ({
          id: record.id,
          optimisticLockVersion: record.optimisticLockVersion,
          startDate: record.startDate,
          endDate: record.endDate,
          gainer1Id: record.gainer1.id,
          gainer1Rate: record.gainer1Rate,
          gainer2Id: record.gainer2?.id,
          gainer2Rate: record.gainer2Rate,
          gainer3Id: record.gainer3?.id,
          gainer3Rate: record.gainer3Rate,
          gainer4Id: record.gainer4?.id,
          gainer4Rate: record.gainer4Rate,
          gainer5Id: record.gainer5?.id,
          gainer5Rate: record.gainer5Rate,
        })),
      } as CreateUpdateLoanContract);
    }
  }, []);

  const handleSaveError = (error: NormalizedError) => {
    setErrorsToForm(form, "contract.attrs", error.violations);
    if (error.violations) {
      setClientsViolationErrors(parseClientsViolationErrors(error.violations));
    }
  };

  const handleFormFinish = () => {
    form
      .validateFields()
      .then((values) => {
        if (clientsViolationErrors.size > 0) {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formError"),
          });
        } else {
          const processedValues = { ...values };

          processedValues.clients = clients.filter((c) => !!c).map((c) => clientToCreateUpdateContractClient(c));
          processedValues.clientIds = [];
          processedValues.policyHolderIndex = 0;
          processedValues.coDebtorIndex = clients[1] ? 1 : undefined;

          delete processedValues.clientIdentifiers;

          if (initialContract) {
            updateContract.mutate(
              { id: initialContract.id, object: processedValues },
              {
                onSuccess: onCancel,
                onError: handleSaveError,
              },
            );
          } else {
            createContract.mutate(processedValues, {
              onError: handleSaveError,
              onSuccess: (data) => {
                navigate(generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: data.id }));
              },
            });
          }
        }
      })
      .catch((errors: ValidateErrorEntity) => {
        notification.error({
          title: t("common.error"),
          description: t("contract.validations.formError"),
        });
        resolveFormValidationError(errors);
      });
  };

  const handleClientChange = (index: number, client?: Partial<Client>) => {
    const updatedClients = [...clients];
    updatedClients[index] = client;
    setClients(updatedClients);

    if (client) {
      clientValidation.mutate(
        { prefix: `clients[${index}]`, client: clientToCreateUpdateContractClient(client) },
        {
          onError: (error) => {
            if (error.violations) {
              const violations = error.violations;
              const indexString = violations[0]?.fieldPath.charAt(8);
              if (!indexString) {
                return;
              }

              const index = parseInt(indexString);
              const updatedViolationsErrors = new Map<number, FieldConstraintViolation[]>([...clientsViolationErrors]);
              updatedViolationsErrors.set(
                index,
                violations.map((v) => ({ ...v, fieldPath: v.fieldPath.substring(18) })),
              );
              setClientsViolationErrors(updatedViolationsErrors);
            }
          },
        },
      );
    }
  };

  return (
    <Form form={form} layout="vertical" name="loanContractForm" onValuesChange={handleContractValidationChange}>
      <HiddenInput name="type" initialValue={ContractType.LOAN_CONTRACT} />
      <HiddenInput name="optimisticLockVersion" />
      <HiddenInput name="status" />

      <LoanContractFormHeaderSection
        initialContract={initialContract}
        clients={clients}
        clientsViolationErrors={clientsViolationErrors}
        onClientChange={handleClientChange}
        onClientViolationErrorsChange={setClientsViolationErrors}
      />

      <LoanContractFormDataSection />

      <ContractFormAgentsSection
        initialContract={initialContract}
        privilegedChangesPermission={Permission.PRIVILEGED_CHANGES_ON_VERIFIED_LOAN}
      />

      <div className="margin-top-small">
        <Button type="primary" icon={<AntIcon type="save" />} onClick={handleFormFinish}>
          {t("common.save")}
        </Button>

        {onCancel && (
          <Button className="margin-left-tiny" onClick={onCancel} icon={<AntIcon type="close" />}>
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </Form>
  );
};
